import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { DollarSign } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ExchangeRateTab = () => {
  const { toast } = useToast();
  const [exchangeRate, setExchangeRate] = useState(1000);
  const [tempExchangeRate, setTempExchangeRate] = useState('1000');

  useEffect(() => {
    fetchExchangeRate();
  }, []);

  const fetchExchangeRate = async () => {
    try {
      const response = await fetch(`${API}/exchange-rate`);
      if (response.ok) {
        const data = await response.json();
        setExchangeRate(data.usd_to_ars);
        setTempExchangeRate(data.usd_to_ars.toString());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const updateExchangeRate = async () => {
    const rateValue = parseFloat(tempExchangeRate);
    if (isNaN(rateValue) || rateValue <= 0) {
      toast({ 
        title: "Error", 
        description: "Ingresa un valor numérico válido mayor a 0", 
        variant: "destructive" 
      });
      return;
    }

    try {
      const response = await fetch(`${API}/exchange-rate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usd_to_ars: rateValue })
      });
      if (response.ok) {
        toast({ title: "Éxito", description: "Cotización actualizada correctamente" });
        await fetchExchangeRate();
      } else {
        toast({ title: "Error", description: "No se pudo actualizar la cotización", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "No se pudo actualizar la cotización", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">Cotización del Dólar</h2>
      
      <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-600 to-cyan-600 flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Cotización actual</p>
              <p className="text-3xl font-bold text-white">
                ${exchangeRate.toLocaleString()} <span className="text-lg text-slate-400">ARS/USD</span>
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Nueva Cotización (ARS por 1 USD)</Label>
              <div className="flex gap-2">
                <Input 
                  type="number" 
                  step="0.01"
                  value={tempExchangeRate}
                  onChange={(e) => setTempExchangeRate(e.target.value)}
                  className="bg-slate-900/50 border-slate-700 text-white" 
                  placeholder="1000.50"
                />
                <Button 
                  onClick={updateExchangeRate}
                  className="bg-gradient-to-r from-cyan-600 to-emerald-600"
                >
                  Actualizar
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 border-blue-700/30">
        <CardContent className="p-6">
          <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
            <span className="text-2xl">💡</span>
            ¿Cómo funciona la conversión USD → ARS?
          </h3>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-cyan-600/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-cyan-400 font-bold">1</span>
              </div>
              <div>
                <p className="text-white font-medium">Productos en USD</p>
                <p className="text-slate-400 text-sm">Al crear/editar productos, selecciona USD como moneda</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-cyan-600/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-cyan-400 font-bold">2</span>
              </div>
              <div>
                <p className="text-white font-medium">Conversión Automática</p>
                <p className="text-slate-400 text-sm">Cuando el cliente elige MercadoPago, se convierte USD→ARS automáticamente</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-cyan-600/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-cyan-400 font-bold">3</span>
              </div>
              <div>
                <p className="text-white font-medium">Cobro en Pesos</p>
                <p className="text-slate-400 text-sm">MercadoPago cobra el monto convertido en ARS según tu cotización</p>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-700 pt-4 mt-4">
            <p className="text-slate-400 text-sm">
              <span className="text-emerald-400 font-semibold">✓ MercadoPago:</span> Convierte automáticamente<br />
              <span className="text-blue-400 font-semibold">✓ PayPal:</span> Cobra directamente en USD (sin conversión)<br />
              <span className="text-yellow-400 font-semibold">✓ Pago en Local:</span> Sin conversión
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-emerald-900/20 to-cyan-900/20 border-emerald-700/30">
        <CardContent className="p-4">
          <p className="text-emerald-400 text-sm font-medium mb-2">💡 Ejemplo práctico:</p>
          <p className="text-slate-300 text-sm">
            Producto: Mouse USB = <span className="font-bold text-white">$50 USD</span><br />
            Cotización: <span className="font-bold text-white">${exchangeRate.toLocaleString()} ARS/USD</span><br />
            Cliente paga con MercadoPago → <span className="font-bold text-cyan-400">${(50 * exchangeRate).toLocaleString()} ARS</span>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExchangeRateTab;
